/**
 * WidgetPicker — modal pro přidání widgetu na panel.
 *
 * Flat seznam nestačí (100+ widgetů). UI:
 *  - fulltext (title / id / module)
 *  - kategorie = modul (chip filter)
 *  - přednostně modul, ve kterém už panel je (widgety na panelu / panel.id)
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers, Puzzle } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { SearchInput } from '../components/ui/SearchInput';
import { cn } from '../lib/utils';
import { REGISTRY, REGISTRY_MAP } from './registry';
import type { WidgetDef, WidgetPanel as Panel } from './types';

const CORE_CAT = '__core__';

function moduleKey(w: WidgetDef): string {
  return w.module ?? CORE_CAT;
}

function moduleLabel(key: string, t: (k: string, d: string) => string): string {
  if (key === CORE_CAT) return t('widgets.catCore', 'Core / systém');
  return key;
}

/** Preferovaný modul z obsahu panelu nebo z id/label. */
export function inferPreferredModule(panel: Panel): string | null {
  const counts = new Map<string, number>();
  for (const inst of panel.widgets) {
    const m = REGISTRY_MAP.get(inst.widgetId)?.module;
    if (m) counts.set(m, (counts.get(m) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [mod, n] of counts) {
    if (n > bestN) {
      best = mod;
      bestN = n;
    }
  }
  if (best) return best;

  const known = new Set<string>();
  for (const w of REGISTRY) {
    if (w.module) known.add(w.module);
    const prefix = w.id.split('.')[0];
    if (prefix) known.add(prefix);
  }
  for (const part of panel.id.toLowerCase().split(/[-_./]+/)) {
    if (known.has(part)) return part;
  }
  const lab = panel.label.toLowerCase();
  for (const m of known) {
    if (m.length >= 3 && lab.includes(m)) return m;
  }
  return null;
}

export function WidgetPicker({
  open,
  onClose,
  panel,
  preferredModule: preferredProp,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  panel: Panel;
  /** Explicitní modul stránky (ModuleWorkspace). Jinak se odvodí z panelu. */
  preferredModule?: string | null;
  onPick: (widgetId: string) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState<string | 'all'>('all');

  const preferred = preferredProp !== undefined
    ? preferredProp
    : inferPreferredModule(panel);

  // Při otevření: reset search, default filtr = preferred modul (pokud je)
  useEffect(() => {
    if (!open) return;
    setQ('');
    setCat(preferred ?? 'all');
  }, [open, preferred]);

  const available = useMemo(
    () => REGISTRY.filter((w) => !panel.widgets.some((i) => i.widgetId === w.id)),
    [panel.widgets],
  );

  const categories = useMemo(() => {
    const map = new Map<string, number>();
    for (const w of available) {
      const k = moduleKey(w);
      map.set(k, (map.get(k) ?? 0) + 1);
    }
    const keys = [...map.keys()].sort((a, b) => {
      if (a === preferred) return -1;
      if (b === preferred) return 1;
      if (a === CORE_CAT) return 1;
      if (b === CORE_CAT) return -1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({ key: k, count: map.get(k) ?? 0 }));
  }, [available, preferred]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return available.filter((w) => {
      if (cat !== 'all' && moduleKey(w) !== cat) return false;
      if (!needle) return true;
      const title = t(`widgets.registry.${w.id}`, w.title);
      const hay = `${title} ${w.id} ${w.module ?? ''} ${w.kind ?? ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [available, cat, q, t]);

  /** Skupiny pro seznam: preferred first, pak abeceda. */
  const groups = useMemo(() => {
    const map = new Map<string, WidgetDef[]>();
    for (const w of filtered) {
      const k = moduleKey(w);
      const arr = map.get(k) ?? [];
      arr.push(w);
      map.set(k, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.title.localeCompare(b.title, 'cs'));
    }
    const keys = [...map.keys()].sort((a, b) => {
      if (a === preferred) return -1;
      if (b === preferred) return 1;
      if (a === CORE_CAT) return 1;
      if (b === CORE_CAT) return -1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({ key: k, widgets: map.get(k)! }));
  }, [filtered, preferred]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('widgets.addWidgetTitle', 'Přidat widget')}
      size='lg'
    >
      <div className='flex flex-col gap-3 max-h-[min(70vh,36rem)]'>
        <SearchInput
          value={q}
          onChange={setQ}
          size='md'
          autoFocus
          className='w-full'
          placeholder={t('widgets.searchPlaceholder', 'Hledat název, id, modul…')}
          resultCount={filtered.length}
          totalCount={available.length}
        />

        <div className='flex flex-wrap items-center gap-1.5'>
          <button
            type='button'
            onClick={() => setCat('all')}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
              cat === 'all'
                ? 'border-blue-500/40 bg-blue-500/15 text-blue-300'
                : 'border-gray-800 bg-gray-900/40 text-gray-400 hover:text-gray-200 hover:border-gray-700',
            )}
          >
            <Layers size={12} />
            {t('widgets.catAll', 'Vše')}
            <span className='opacity-70'>{available.length}</span>
          </button>
          {categories.map(({ key, count }) => {
            const isPref = key === preferred;
            const active = cat === key;
            return (
              <button
                key={key}
                type='button'
                onClick={() => setCat(key)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                  active
                    ? isPref
                      ? 'border-purple-500/40 bg-purple-500/15 text-purple-300'
                      : 'border-blue-500/40 bg-blue-500/15 text-blue-300'
                    : isPref
                    ? 'border-purple-500/25 bg-purple-500/5 text-purple-300/90 hover:bg-purple-500/10'
                    : 'border-gray-800 bg-gray-900/40 text-gray-400 hover:text-gray-200 hover:border-gray-700',
                )}
                title={isPref ? t('widgets.preferredModule', 'Modul této stránky') : undefined}
              >
                {moduleLabel(key, t)}
                {isPref && (
                  <span className='text-[9px] uppercase tracking-wide opacity-80'>
                    {t('widgets.here', 'zde')}
                  </span>
                )}
                <span className='opacity-70'>{count}</span>
              </button>
            );
          })}
        </div>

        <div className='min-h-0 flex-1 overflow-y-auto space-y-4 pr-0.5'>
          {filtered.length === 0 && (
            <p className='text-sm text-gray-500 py-6 text-center'>
              {available.length === 0
                ? t('widgets.allOnPanel', 'Všechny widgety už na panelu jsou.')
                : t('widgets.noMatch', 'Nic neodpovídá hledání.')}
            </p>
          )}

          {groups.map(({ key, widgets }) => {
            const isPref = key === preferred;
            return (
              <section key={key} className='space-y-1.5'>
                <div className='sticky top-0 z-[1] flex items-center gap-2 bg-gray-950/95 backdrop-blur-sm py-1 border-b border-gray-800/50'>
                  <span
                    className={cn(
                      'text-[11px] font-semibold uppercase tracking-wider',
                      isPref ? 'text-purple-300' : 'text-gray-500',
                    )}
                  >
                    {moduleLabel(key, t)}
                  </span>
                  {isPref && (
                    <Badge variant='purple'>
                      {t('widgets.fromThisModule', 'z tohoto modulu')}
                    </Badge>
                  )}
                  <span className='text-[10px] text-gray-600 ml-auto'>{widgets.length}</span>
                </div>
                <div className='space-y-1'>
                  {widgets.map((w) => (
                    <button
                      key={w.id}
                      type='button'
                      onClick={() => onPick(w.id)}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left transition-colors',
                        isPref
                          ? 'border-purple-500/20 bg-purple-500/5 hover:border-purple-500/40 hover:bg-purple-500/10'
                          : 'border-gray-800 hover:border-blue-500/40 hover:bg-gray-800/40',
                      )}
                    >
                      <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-800 bg-gray-900/80 text-gray-400'>
                        {w.icon
                          ? <w.icon size={15} />
                          : <Puzzle size={15} />}
                      </span>
                      <span className='min-w-0 flex-1'>
                        <span className='block text-sm text-gray-100 truncate'>
                          {t(`widgets.registry.${w.id}`, w.title)}
                        </span>
                        <span className='block text-[10px] font-mono text-gray-500 truncate'>
                          {w.id}
                        </span>
                      </span>
                      <div className='flex items-center gap-1 shrink-0'>
                        {w.kind && w.kind !== 'custom' && (
                          <Badge variant='gray'>{w.kind}</Badge>
                        )}
                        <Badge variant='gray'>{w.format}</Badge>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
